import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { MessageSquare, X, Send, Circle, Loader2, Search, ArrowLeft, Paperclip, Image as ImageIcon, FileText, Download, Users, Clock, Smile, Mic, AtSign, StopCircle, Trash2, Pin, PinOff, Sparkles, Eye } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMOJI_REACTIONS = ["👍", "❤️", "✅", "😂", "🙏", "🎉", "🔥", "👀"];

/**
 * InternalChatWidget — floating team-chat for admin + ops staff.
 *
 * Views (Slack-style):
 *   1. Recent    — last conversations with unread count + preview (default)
 *   2. People    — full staff directory (search people)
 *   3. Mentions  — messages that @mention you
 *   4. Thread    — active conversation
 *
 * Features:
 *   • Short-poll every 4s (open) / 10s (closed) via GET /internal-chat/updates
 *   • Toast + unread-badge on incoming messages from a NON-active thread
 *   • File attachments (25 MB cap) + voice notes (press-and-hold the mic)
 *   • Reactions: 👍 ❤️ ✅ 😂 🙏 🎉 🔥 👀 on any bubble
 *   • @mentions autocomplete + dedicated "Mentions" filter + offline WhatsApp ping
 *   • Full message history persisted in Mongo (`internal_messages`) and loaded
 *     on each thread open — nothing is lost on refresh or logout
 */
const InternalChatWidget = ({ authAxios, basePath = "/api" }) => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("recent"); // recent | people | mentions | thread
  const [me, setMe] = useState(null);
  const [directory, setDirectory] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);

  // Reactions popover
  const [reactPickerFor, setReactPickerFor] = useState(null);

  // Mentions autocomplete
  const [mentionState, setMentionState] = useState({ open: false, query: "" });

  // Voice recording
  const [recording, setRecording] = useState(false);
  const [recordedSecs, setRecordedSecs] = useState(0);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordTimerRef = useRef(null);

  // Pinned messages (per thread)
  const [pinnedMessages, setPinnedMessages] = useState([]);
  // Smart Reply suggestions (LLM-powered)
  const [smartReplies, setSmartReplies] = useState([]);
  // Can the current user pin? (admin/manager role)
  const [canPin, setCanPin] = useState(false);

  const lastFetchRef = useRef(null);
  const activeRef = useRef(null);
  const openRef = useRef(false);
  const meRef = useRef(null);
  const msgEndRef = useRef(null);
  const inputElRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => { activeRef.current = active; }, [active]);
  useEffect(() => { openRef.current = open; }, [open]);
  useEffect(() => { meRef.current = me; }, [me]);

  // ── Data loaders ──────────────────────────────────────────────
  const fetchDirectory = useCallback(async () => {
    try {
      const r = await authAxios.get(`${API_URL}${basePath}/internal-chat/directory`);
      setMe(r.data?.me || null);
      setDirectory(r.data?.items || []);
    } catch { /* silent */ }
  }, [authAxios, basePath]);

  const fetchConversations = useCallback(async () => {
    try {
      const r = await authAxios.get(`${API_URL}${basePath}/internal-chat/conversations`);
      setConversations(r.data?.items || []);
    } catch { /* silent */ }
  }, [authAxios, basePath]);

  const fetchMentions = useCallback(async () => {
    try {
      const r = await authAxios.get(`${API_URL}${basePath}/internal-chat/mentions`);
      setMentions(r.data?.items || []);
    } catch { /* silent */ }
  }, [authAxios, basePath]);

  const fetchMessages = useCallback(async (otherKey) => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}${basePath}/internal-chat/messages/${encodeURIComponent(otherKey)}`);
      setMessages(r.data?.items || []);
    } catch { /* silent */ }
    setLoading(false);
    setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: "smooth" }), 60);
  }, [authAxios, basePath]);

  const fetchPinned = useCallback(async (otherKey) => {
    if (!otherKey || otherKey.startsWith("@")) { setPinnedMessages([]); return; }
    try {
      const r = await authAxios.get(`${API_URL}${basePath}/internal-chat/pinned/${encodeURIComponent(otherKey)}`);
      setPinnedMessages(r.data?.items || []);
    } catch { setPinnedMessages([]); }
  }, [authAxios, basePath]);

  const fetchSmartReplies = useCallback(async (otherKey) => {
    if (!otherKey || otherKey.startsWith("@")) { setSmartReplies([]); return; }
    try {
      const r = await authAxios.get(`${API_URL}${basePath}/internal-chat/smart-reply/${encodeURIComponent(otherKey)}`);
      setSmartReplies(r.data?.suggestions || []);
    } catch { setSmartReplies([]); }
  }, [authAxios, basePath]);

  // Shadow-refetch active thread (for reactions / edits added by peers)
  const refetchActive = useCallback(async () => {
    const a = activeRef.current;
    if (!a || a.user_key.startsWith("@")) return;
    try {
      const r = await authAxios.get(`${API_URL}${basePath}/internal-chat/messages/${encodeURIComponent(a.user_key)}`);
      const incoming = r.data?.items || [];
      setMessages((prev) => {
        // Only replace if the set of (id, reactions-signature) actually changed — keeps scroll steady.
        const sig = (arr) => arr.map((m) => `${m.id}:${(m.reactions || []).length}`).join("|");
        return sig(prev) === sig(incoming) ? prev : incoming;
      });
    } catch { /* silent */ }
  }, [authAxios, basePath]);

  // ── Short-poll loop (unread, incoming toasts, reactions sync) ──
  useEffect(() => {
    let cancelled = false;
    let timer;
    const poll = async () => {
      try {
        const r = await authAxios.get(`${API_URL}${basePath}/internal-chat/updates`, {
          params: lastFetchRef.current ? { since: lastFetchRef.current } : {},
        });
        if (cancelled) return;
        lastFetchRef.current = r.data?.server_time || lastFetchRef.current;
        setUnread(r.data?.unread_total || 0);
        const incoming = r.data?.items || [];
        if (incoming.length) {
          const activeKey = activeRef.current?.user_key;
          const mine = activeKey ? incoming.filter((m) => m.from_user === activeKey) : [];
          if (mine.length) {
            setMessages((prev) => {
              const seen = new Set(prev.map((p) => p.id));
              const fresh = mine.filter((m) => !seen.has(m.id));
              return fresh.length ? [...prev, ...fresh] : prev;
            });
            setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: "smooth" }), 40);
          }
          const otherThreads = activeKey ? incoming.filter((m) => m.from_user !== activeKey) : incoming;
          const myKey = meRef.current?.user_key;
          if (otherThreads.length) {
            otherThreads.slice(-3).forEach((m) => {
              const isMention = myKey && (m.mentions || []).includes(myKey);
              toast(`${isMention ? "🏷️ Mentioned by " : "💬 "}${m.from_name || m.from_user}`, {
                description: (m.body || "").slice(0, 110) + ((m.body || "").length > 110 ? "…" : ""),
                action: {
                  label: "Open",
                  onClick: () => {
                    setOpen(true);
                    const peer = directory.find((d) => d.user_key === m.from_user)
                      || { user_key: m.from_user, name: m.from_name, user_type: m.from_type, role: "", online: true };
                    openConvo(peer);
                  },
                },
                duration: 6000,
              });
            });
            fetchConversations();
            fetchMentions();
          }
        }
        // Sync reactions / edits for the open thread
        if (openRef.current && activeRef.current) refetchActive();
      } catch { /* silent */ }
      timer = setTimeout(poll, openRef.current ? 4000 : 10000);
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authAxios, basePath, directory]);

  useEffect(() => {
    if (open) {
      if (!me) fetchDirectory();
      fetchConversations();
      fetchMentions();
    }
  }, [open, me, fetchDirectory, fetchConversations, fetchMentions]);

  // ── Open conversation ──────────────────────────────────────────
  const openConvo = useCallback(async (user) => {
    setActive(user);
    setMessages([]);
    setPending([]);
    setView("thread");
    setReactPickerFor(null);
    setSmartReplies([]);
    setPinnedMessages([]);
    if (!user.user_key.startsWith("@")) {
      await fetchMessages(user.user_key);
      fetchPinned(user.user_key);
      fetchSmartReplies(user.user_key);
    }
  }, [fetchMessages, fetchPinned, fetchSmartReplies]);

  // ── Pin / unpin ────────────────────────────────────────────────
  // Figure out whether the current user is admin+manager role (lazily).
  useEffect(() => {
    // Admin surface uses basePath "/api"; ops uses "/api/ops". Ops staff can't pin.
    setCanPin(basePath === "/api");
  }, [basePath]);

  const togglePin = async (msg) => {
    const method = msg.pinned ? "delete" : "post";
    try {
      await authAxios[method](`${API_URL}${basePath}/internal-chat/messages/${msg.id}/pin`);
      // Optimistic: flip flag locally + refetch pinned list
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, pinned: !msg.pinned } : m)));
      if (active) fetchPinned(active.user_key);
      toast(msg.pinned ? "Unpinned" : "Pinned to top");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update pin");
    }
  };

  // ── Attachments ────────────────────────────────────────────────
  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    const next = [...pending];
    for (const f of files) {
      if (f.size > 25 * 1024 * 1024) { toast.error(`${f.name} is bigger than 25 MB`); continue; }
      const form = new FormData();
      form.append("file", f);
      try {
        const r = await authAxios.post(`${API_URL}${basePath}/internal-chat/upload`, form, { headers: { "Content-Type": "multipart/form-data" } });
        next.push(r.data);
      } catch (e) { toast.error(e?.response?.data?.detail || `Failed to upload ${f.name}`); }
    }
    setPending(next);
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removePending = (id) => setPending((prev) => prev.filter((a) => a.id !== id));

  // ── Voice recording (MediaRecorder, press-and-hold) ────────────
  const startRecording = async () => {
    if (recording || !navigator.mediaDevices?.getUserMedia) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"]
        .find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recordedChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data?.size) recordedChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(recordedChunksRef.current, { type: mr.mimeType || "audio/webm" });
        recordedChunksRef.current = [];
        if (blob.size < 400) { toast("Recording too short — hold the mic a bit longer"); return; }
        const ext = (mr.mimeType || "").includes("mp4") ? "m4a" : "webm";
        const file = new File([blob], `voice-${Date.now()}.${ext}`, { type: blob.type });
        await uploadFiles([file]);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setRecordedSecs(0);
      recordTimerRef.current = setInterval(() => {
        setRecordedSecs((s) => {
          if (s >= 120) { stopRecording(); return s; } // auto-cap at 2 min
          return s + 1;
        });
      }, 1000);
    } catch {
      toast.error("Microphone permission denied");
    }
  };

  const stopRecording = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (recordTimerRef.current) { clearInterval(recordTimerRef.current); recordTimerRef.current = null; }
    if (mr && mr.state !== "inactive") mr.stop();
    mediaRecorderRef.current = null;
    setRecording(false);
    setRecordedSecs(0);
  }, []);

  const cancelRecording = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (recordTimerRef.current) { clearInterval(recordTimerRef.current); recordTimerRef.current = null; }
    if (mr && mr.state !== "inactive") {
      // overwrite onstop so we don't upload
      mr.onstop = () => { mr.stream.getTracks().forEach((t) => t.stop()); };
      mr.stop();
    }
    recordedChunksRef.current = [];
    mediaRecorderRef.current = null;
    setRecording(false);
    setRecordedSecs(0);
  }, []);

  useEffect(() => () => { if (recordTimerRef.current) clearInterval(recordTimerRef.current); }, []);

  // ── Reactions ──────────────────────────────────────────────────
  const toggleReaction = async (messageId, emoji) => {
    setReactPickerFor(null);
    try {
      const r = await authAxios.post(`${API_URL}${basePath}/internal-chat/messages/${messageId}/react`, { emoji });
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, reactions: r.data?.reactions || [] } : m)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not react");
    }
  };

  // ── Mentions autocomplete ──────────────────────────────────────
  const handleBodyChange = (e) => {
    const value = e.target.value;
    setBody(value);
    const caret = e.target.selectionStart;
    // Find last unclosed @word before caret
    const upToCaret = value.slice(0, caret);
    const match = upToCaret.match(/@([A-Za-z0-9_.@-]*)$/);
    if (match) setMentionState({ open: true, query: match[1].toLowerCase() });
    else setMentionState({ open: false, query: "" });
  };

  const insertMention = (user) => {
    const el = inputElRef.current;
    if (!el) return;
    const caret = el.selectionStart || body.length;
    const upToCaret = body.slice(0, caret);
    const after = body.slice(caret);
    const replaced = upToCaret.replace(/@([A-Za-z0-9_.@-]*)$/, `@${user.user_key} `);
    const nextBody = replaced + after;
    setBody(nextBody);
    setMentionState({ open: false, query: "" });
    setTimeout(() => {
      el.focus();
      const pos = replaced.length;
      try { el.setSelectionRange(pos, pos); } catch { /* ignore */ }
    }, 0);
  };

  const mentionCandidates = useMemo(() => {
    if (!mentionState.open) return [];
    const q = mentionState.query;
    const myKey = meRef.current?.user_key;
    return directory
      .filter((d) => !d.user_key.startsWith("@") && d.user_key !== myKey)
      .filter((d) => !q
        || (d.user_key || "").toLowerCase().includes(q)
        || (d.name || "").toLowerCase().includes(q))
      .slice(0, 5);
  }, [mentionState, directory]);

  // ── Send ───────────────────────────────────────────────────────
  const send = async () => {
    if (sending) return;
    const text = body.trim();
    if (!text && pending.length === 0) return;
    if (!active) return;
    setSending(true);
    try {
      const r = await authAxios.post(`${API_URL}${basePath}/internal-chat/send`, {
        to_user: active.user_key,
        body: text || (pending.length ? "📎 Attachment" : ""),
        attachments: pending,
      });
      if (r.data?.broadcast) {
        setMessages((prev) => [...prev, {
          id: `bcast-${Date.now()}`,
          from_user: me?.user_key, from_name: me?.name,
          broadcast_tag: r.data.tag,
          body: `📣 Broadcast · delivered to ${r.data.count} people · WhatsApp pinged ${r.data.whatsapp_attempted}`,
          attachments: pending,
          created_at: r.data.created_at,
        }, {
          id: `bcast-body-${Date.now()}`,
          from_user: me?.user_key, from_name: me?.name,
          body: text, attachments: pending,
          created_at: r.data.created_at,
        }]);
      } else {
        setMessages((prev) => [...prev, r.data]);
      }
      setBody("");
      setPending([]);
      setMentionState({ open: false, query: "" });
      setSmartReplies([]);
      fetchConversations();
      // After a short pause, regenerate smart replies for the next incoming turn.
      setTimeout(() => { if (activeRef.current) fetchSmartReplies(activeRef.current.user_key); }, 2500);
      setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: "smooth" }), 40);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    }
    setSending(false);
  };

  // ── Derived lists ─────────────────────────────────────────────
  const dirMap = useMemo(() => {
    const m = new Map();
    directory.forEach((d) => m.set(d.user_key, d));
    return m;
  }, [directory]);

  const filteredPeople = useMemo(() => {
    if (!search.trim()) return directory;
    const q = search.toLowerCase();
    return directory.filter((d) =>
      (d.name || "").toLowerCase().includes(q)
      || (d.role || "").toLowerCase().includes(q)
      || (d.user_key || "").toLowerCase().includes(q),
    );
  }, [directory, search]);

  const recentWithMeta = useMemo(() => conversations.map((c) => {
    const peer = dirMap.get(c.other_user_key) || {
      user_key: c.other_user_key,
      name: c.last_message?.from_user === c.other_user_key ? c.last_message?.from_name : c.other_user_key,
      role: "", user_type: c.last_message?.from_type || "admin", online: false,
    };
    return { ...c, peer };
  }), [conversations, dirMap]);

  // ── Render ────────────────────────────────────────────────────
  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-[60] bg-gradient-to-br from-[#1E1B4B] to-[#6366F1] text-white rounded-full w-14 h-14 shadow-2xl flex items-center justify-center hover:scale-105 transition"
        data-testid="internal-chat-fab"
        title="Team Chat"
      >
        <MessageSquare size={22} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-[#E83E8C] text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5" data-testid="internal-chat-unread-badge">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-[65] w-[380px] h-[580px] max-h-[85vh] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          data-testid="internal-chat-panel">
          {/* Header */}
          <div className="p-3 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-[#1E1B4B] to-[#6366F1] text-white">
            <div className="flex items-center gap-2 min-w-0">
              {view === "thread" && active && (
                <button
                  onClick={() => { setActive(null); setView("recent"); fetchConversations(); }}
                  className="p-1 hover:bg-white/10 rounded"
                  data-testid="chat-back-btn"
                ><ArrowLeft size={16} /></button>
              )}
              <div className="min-w-0">
                <p className="text-sm font-bold flex items-center gap-1 truncate">
                  <MessageSquare size={14} /> {active ? active.name : "Team Chat"}
                </p>
                <p className="text-[10px] opacity-80 truncate">
                  {active ? (active.online ? "online now" : "offline") : "Recorded internal · super-admin can audit"}
                </p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/10 rounded flex-shrink-0" data-testid="close-chat"><X size={16} /></button>
          </div>

          {/* Tabs */}
          {view !== "thread" && (
            <div className="flex border-b border-gray-100 bg-gray-50">
              <TabBtn active={view === "recent"} onClick={() => setView("recent")} icon={<Clock size={12} />} label="Recent" testid="chat-tab-recent" />
              <TabBtn active={view === "mentions"} onClick={() => setView("mentions")} icon={<AtSign size={12} />} label="Mentions" badge={mentions.length} testid="chat-tab-mentions" />
              <TabBtn active={view === "people"} onClick={() => setView("people")} icon={<Users size={12} />} label="People" testid="chat-tab-people" />
            </div>
          )}

          {/* RECENT */}
          {view === "recent" && (
            <div className="flex-1 overflow-y-auto" data-testid="chat-recent-list">
              {recentWithMeta.length === 0 && (
                <div className="text-center p-6">
                  <MessageSquare size={28} className="mx-auto text-gray-300 mb-2" />
                  <p className="text-xs text-gray-500 mb-1">No conversations yet.</p>
                  <button onClick={() => setView("people")} className="text-xs text-indigo-600 font-semibold hover:underline" data-testid="start-first-chat">Start your first chat →</button>
                </div>
              )}
              <div className="divide-y divide-gray-100">
                {recentWithMeta.map((c) => (
                  <button key={c.conversation_id} onClick={() => openConvo(c.peer)}
                    className="w-full text-left flex items-center gap-2 px-3 py-2.5 hover:bg-gray-50"
                    data-testid={`recent-${c.other_user_key}`}>
                    <div className="relative flex-shrink-0">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold">
                        {c.peer?.name?.[0]?.toUpperCase() || "?"}
                      </div>
                      {c.peer?.online && <Circle size={9} fill="#10B981" stroke="none" className="absolute -bottom-0.5 -right-0.5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <p className="text-xs font-semibold truncate">{c.peer?.name || c.other_user_key}</p>
                        <span className="text-[9px] text-gray-400 flex-shrink-0">
                          {c.last_message?.created_at ? new Date(c.last_message.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" }) : ""}
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-500 truncate">
                        {c.last_message?.from_user === meRef.current?.user_key ? "You: " : ""}
                        {c.last_message?.body || (c.last_message?.attachments?.length ? "📎 Attachment" : "")}
                      </p>
                    </div>
                    {c.unread > 0 && <span className="bg-[#E83E8C] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1.5 flex-shrink-0">{c.unread > 99 ? "99+" : c.unread}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* MENTIONS */}
          {view === "mentions" && (
            <div className="flex-1 overflow-y-auto" data-testid="chat-mentions-list">
              {mentions.length === 0 ? (
                <div className="text-center p-6">
                  <AtSign size={28} className="mx-auto text-gray-300 mb-2" />
                  <p className="text-xs text-gray-500">No mentions yet.</p>
                  <p className="text-[10px] text-gray-400 mt-1">When a teammate types <code className="bg-gray-100 px-1 rounded">@your_key</code> you'll see it here.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {mentions.map((m) => {
                    const peer = dirMap.get(m.from_user) || { user_key: m.from_user, name: m.from_name, user_type: m.from_type, role: "", online: false };
                    return (
                      <button key={m.id} onClick={() => openConvo(peer)} className="w-full text-left px-3 py-2 hover:bg-gray-50" data-testid={`mention-${m.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs font-semibold truncate flex items-center gap-1"><AtSign size={10} className="text-indigo-500" /> {m.from_name || m.from_user}</p>
                          <span className="text-[9px] text-gray-400">{m.created_at ? new Date(m.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", timeZone: "Asia/Kolkata" }) : ""}</span>
                        </div>
                        <p className="text-[11px] text-gray-600 line-clamp-2 mt-0.5">{m.body}</p>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* PEOPLE */}
          {view === "people" && (
            <div className="flex-1 overflow-y-auto">
              <div className="relative p-2">
                <Search size={12} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search people…"
                  className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg" data-testid="chat-search" />
              </div>
              <div className="divide-y divide-gray-100">
                {filteredPeople.length === 0 && <p className="text-xs text-[#94a3b8] text-center py-4">No staff found.</p>}
                {filteredPeople.map((d) => (
                  <button key={d.user_key} onClick={() => openConvo(d)}
                    className="w-full text-left flex items-center gap-2 px-3 py-2 hover:bg-gray-50" data-testid={`chat-user-${d.user_key}`}>
                    <div className="relative flex-shrink-0">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold">
                        {d.name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <Circle size={8} fill={d.online ? "#10B981" : "#D1D5DB"} stroke="none" className="absolute -bottom-0.5 -right-0.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate">{d.name}</p>
                      <p className="text-[10px] text-gray-400 truncate">{d.role} · {d.user_type}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* THREAD */}
          {view === "thread" && active && (
            <>
              {active.user_key.startsWith("@") && (
                <div className="bg-amber-50 border-b border-amber-200 px-3 py-2 text-[11px] text-amber-800">
                  📣 <span className="font-bold">Broadcast mode</span> — fan-out to every matching staff member as individual DM + WhatsApp.
                </div>
              )}
              {pinnedMessages.length > 0 && (
                <div className="bg-amber-50 border-b border-amber-200 px-3 py-1.5 text-[11px] text-amber-900 max-h-[88px] overflow-y-auto space-y-1" data-testid="chat-pinned-bar">
                  {pinnedMessages.map((p) => (
                    <div key={p.id} className="flex items-start gap-1.5">
                      <Pin size={10} className="text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="flex-1 line-clamp-2" title={p.body}>
                        <span className="font-semibold">{p.from_name || p.from_user}:</span> {p.body}
                      </p>
                      {canPin && (
                        <button onClick={() => togglePin({ ...p, pinned: true })} className="text-amber-600 hover:text-red-500 flex-shrink-0" title="Unpin" data-testid={`unpin-${p.id}`}>
                          <PinOff size={10} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="flex-1 overflow-y-auto p-3 bg-gray-50" onClick={() => setReactPickerFor(null)}>
                {loading ? (
                  <div className="flex justify-center py-4"><Loader2 size={16} className="animate-spin text-indigo-500" /></div>
                ) : messages.length === 0 ? (
                  <p className="text-xs text-[#94a3b8] text-center py-4">No messages yet. Say hi 👋</p>
                ) : (
                  <div className="space-y-2">
                    {messages.map((m) => {
                      const mine = me && m.from_user === me.user_key;
                      return (
                        <MessageRow
                          key={m.id}
                          msg={m}
                          mine={mine}
                          me={me}
                          authAxios={authAxios}
                          basePath={basePath}
                          canPin={canPin}
                          reactPickerFor={reactPickerFor}
                          setReactPickerFor={setReactPickerFor}
                          onReact={toggleReaction}
                          onPin={togglePin}
                        />
                      );
                    })}
                    <div ref={msgEndRef} />
                  </div>
                )}
              </div>

              {/* Smart Reply suggestions */}
              {smartReplies.length > 0 && pending.length === 0 && !recording && (
                <div className="flex gap-1 overflow-x-auto border-t border-gray-100 bg-gradient-to-r from-indigo-50 to-pink-50 px-2 py-1.5" data-testid="chat-smart-replies">
                  <span className="flex items-center gap-0.5 text-[10px] text-indigo-500 font-bold flex-shrink-0 pr-1">
                    <Sparkles size={10} /> Smart Reply
                  </span>
                  {smartReplies.map((s, i) => (
                    <button
                      key={`${i}-${s}`}
                      onClick={() => { setBody(s); setSmartReplies([]); inputElRef.current?.focus(); }}
                      className="flex-shrink-0 text-[11px] px-2.5 py-1 rounded-full bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-100 whitespace-nowrap"
                      data-testid={`smart-reply-${i}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}

              {/* Pending attachments */}
              {pending.length > 0 && (
                <div className="flex gap-1 overflow-x-auto border-t border-gray-100 bg-gray-50 px-2 py-1.5" data-testid="chat-pending-attachments">
                  {pending.map((a) => (
                    <div key={a.id} className="relative flex items-center gap-1 bg-white border border-gray-200 rounded-lg px-2 py-1 flex-shrink-0">
                      {a.kind === "image" ? <ImageIcon size={12} className="text-indigo-500" />
                        : a.kind === "audio" ? <Mic size={12} className="text-indigo-500" />
                        : <FileText size={12} className="text-indigo-500" />}
                      <span className="text-[10px] font-medium truncate max-w-[90px]">{a.filename}</span>
                      <button onClick={() => removePending(a.id)} className="text-gray-400 hover:text-red-500" data-testid={`remove-${a.id}`}><X size={11} /></button>
                    </div>
                  ))}
                </div>
              )}

              {/* Recording indicator */}
              {recording && (
                <div className="flex items-center justify-between gap-2 border-t border-red-200 bg-red-50 px-3 py-1.5" data-testid="chat-recording-bar">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-[11px] font-semibold text-red-700">Recording… {Math.floor(recordedSecs / 60)}:{String(recordedSecs % 60).padStart(2, "0")}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={cancelRecording} className="text-red-500 hover:text-red-700 p-1" title="Cancel" data-testid="chat-recording-cancel"><Trash2 size={13} /></button>
                    <button onClick={stopRecording} className="text-red-500 hover:text-red-700 p-1" title="Send voice note" data-testid="chat-recording-stop"><StopCircle size={14} /></button>
                  </div>
                </div>
              )}

              {/* Mentions autocomplete */}
              {mentionState.open && mentionCandidates.length > 0 && (
                <div className="border-t border-gray-100 bg-white max-h-[150px] overflow-y-auto" data-testid="chat-mention-popover">
                  {mentionCandidates.map((d) => (
                    <button key={d.user_key} onClick={() => insertMention(d)}
                      className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-indigo-50 text-left"
                      data-testid={`mention-candidate-${d.user_key}`}>
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-pink-500 flex items-center justify-center text-white text-[10px] font-bold">
                        {d.name?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold truncate">{d.name}</p>
                        <p className="text-[9px] text-gray-500 truncate">@{d.user_key}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Composer */}
              <div className="p-2 border-t border-gray-100 flex items-center gap-1">
                <input ref={fileInputRef} type="file" multiple className="hidden"
                  onChange={(e) => uploadFiles(e.target.files)}
                  accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,audio/*,video/mp4,video/webm,video/quicktime"
                  data-testid="chat-file-input" />
                <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading || recording}
                  className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 flex-shrink-0"
                  title="Attach file" data-testid="chat-attach-btn">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />}
                </button>
                <button type="button"
                  onMouseDown={startRecording}
                  onMouseUp={stopRecording}
                  onMouseLeave={() => { if (recording) stopRecording(); }}
                  onTouchStart={startRecording}
                  onTouchEnd={stopRecording}
                  disabled={uploading}
                  className={`p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-40 flex-shrink-0 ${recording ? "text-red-500 bg-red-50" : "text-gray-500"}`}
                  title="Hold to record a voice note" data-testid="chat-mic-btn">
                  <Mic size={14} />
                </button>
                <input
                  ref={inputElRef}
                  value={body}
                  onChange={handleBodyChange}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Type a message — try @name"
                  className="flex-1 px-3 py-1.5 text-xs border border-gray-200 rounded-xl outline-none focus:border-indigo-400"
                  data-testid="chat-input" />
                <button onClick={send} disabled={sending || (!body.trim() && pending.length === 0)}
                  className="px-3 py-1.5 rounded-xl bg-indigo-500 text-white disabled:opacity-50 flex-shrink-0"
                  data-testid="chat-send-btn">
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// Message row — bubble + reactions + attachments + read-receipts + pin
// ─────────────────────────────────────────────────────────────
const MessageRow = ({ msg, mine, me, authAxios, basePath = "/api", canPin, reactPickerFor, setReactPickerFor, onReact, onPin }) => {
  const [readers, setReaders] = useState(null);
  const [readersOpen, setReadersOpen] = useState(false);

  // Group reactions by emoji + highlight if I reacted
  const reactionGroups = useMemo(() => {
    const map = new Map();
    (msg.reactions || []).forEach((r) => {
      const arr = map.get(r.emoji) || [];
      arr.push(r);
      map.set(r.emoji, arr);
    });
    return Array.from(map.entries()).map(([emoji, rs]) => ({
      emoji, count: rs.length,
      mine: me && rs.some((r) => r.user_key === me.user_key),
      names: rs.map((r) => r.user_name || r.user_key).join(", "),
    }));
  }, [msg.reactions, me]);

  // Readers = read_by minus the sender themselves
  const readerCount = useMemo(() => {
    const list = (msg.read_by || []).filter((k) => k !== msg.from_user);
    return list.length;
  }, [msg.read_by, msg.from_user]);

  const loadReaders = async () => {
    setReadersOpen((p) => !p);
    if (readers || String(msg.id).startsWith("bcast-")) return;
    try {
      const r = await authAxios.get(`${API_URL}${basePath}/internal-chat/messages/${msg.id}/readers`);
      setReaders(r.data?.readers || []);
    } catch { setReaders([]); }
  };

  const pickerOpen = reactPickerFor === msg.id;

  return (
    <div className={`group flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${msg.id}`}>
      <div className="relative max-w-[82%]">
        <div className={`rounded-2xl px-3 py-1.5 text-[12px] ${msg.pinned ? "ring-2 ring-amber-400" : ""} ${mine ? "bg-[#E83E8C] text-white" : "bg-white border border-gray-200 text-gray-800"}`}>
          {!mine && msg.from_name && <p className="text-[10px] font-bold mb-0.5 text-indigo-500 flex items-center gap-1">
            {msg.from_name}
            {msg.pinned && <Pin size={9} className="text-amber-500" />}
          </p>}
          {msg.body && <p className="whitespace-pre-wrap break-words">{renderBodyWithMentions(msg.body, me?.user_key, mine)}</p>}
          {(msg.attachments || []).length > 0 && (
            <div className="mt-1 space-y-1">
              {msg.attachments.map((a) => <AttachmentBubble key={a.id} attachment={a} authAxios={authAxios} mine={mine} />)}
            </div>
          )}
          <div className={`flex items-center gap-1.5 mt-0.5 ${mine ? "justify-end" : "justify-start"}`}>
            <p className={`text-[9px] ${mine ? "text-white/70" : "text-gray-400"}`}>
              {msg.created_at ? new Date(msg.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" }) : ""}
            </p>
            {/* Read-receipt — only on my own messages, only if someone else read it */}
            {mine && readerCount > 0 && !String(msg.id).startsWith("bcast-") && (
              <button
                onClick={(e) => { e.stopPropagation(); loadReaders(); }}
                className="flex items-center gap-0.5 text-[9px] text-white/80 hover:text-white"
                title="Who read this"
                data-testid={`read-by-${msg.id}`}
              >
                <Eye size={9} /> {readerCount}
              </button>
            )}
          </div>
        </div>

        {/* Readers popover */}
        {readersOpen && (
          <div
            onClick={(e) => e.stopPropagation()}
            className={`absolute z-10 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg px-2 py-1.5 min-w-[140px] ${mine ? "right-0" : "left-0"}`}
            data-testid={`readers-popover-${msg.id}`}
          >
            <p className="text-[9px] font-bold text-gray-500 uppercase tracking-wide mb-0.5">Seen by</p>
            {readers === null ? (
              <Loader2 size={12} className="animate-spin text-indigo-500" />
            ) : readers.length === 0 ? (
              <p className="text-[10px] text-gray-400">No one yet</p>
            ) : (
              <div className="space-y-0.5">
                {readers.map((r) => (
                  <div key={r.user_key} className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded-full bg-gradient-to-br from-indigo-500 to-pink-500 flex items-center justify-center text-white text-[8px] font-bold">
                      {r.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <span className="text-[10px] text-gray-700 truncate">{r.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Reaction chips */}
        {reactionGroups.length > 0 && (
          <div className={`flex flex-wrap gap-1 mt-1 ${mine ? "justify-end" : "justify-start"}`}>
            {reactionGroups.map((g) => (
              <button key={g.emoji} onClick={() => onReact(msg.id, g.emoji)} title={g.names}
                className={`text-[11px] rounded-full px-1.5 py-0.5 border transition-colors ${g.mine ? "bg-indigo-100 border-indigo-300 text-indigo-700" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"}`}
                data-testid={`reaction-chip-${msg.id}-${g.emoji}`}>
                {g.emoji} {g.count}
              </button>
            ))}
          </div>
        )}

        {/* Hover actions: React + Pin */}
        {!String(msg.id).startsWith("bcast-") && (
          <div className={`absolute top-0 ${mine ? "-left-16" : "-right-16"} flex items-center gap-1 opacity-0 group-hover:opacity-100`}>
            <button
              onClick={(e) => { e.stopPropagation(); setReactPickerFor(pickerOpen ? null : msg.id); }}
              className="p-1 rounded-full bg-white border border-gray-200 shadow hover:bg-gray-50"
              title="React"
              data-testid={`react-btn-${msg.id}`}
            >
              <Smile size={12} className="text-gray-500" />
            </button>
            {canPin && (
              <button
                onClick={(e) => { e.stopPropagation(); onPin(msg); }}
                className={`p-1 rounded-full bg-white border border-gray-200 shadow hover:bg-amber-50 ${msg.pinned ? "text-amber-500" : "text-gray-500"}`}
                title={msg.pinned ? "Unpin" : "Pin to top"}
                data-testid={`pin-btn-${msg.id}`}
              >
                {msg.pinned ? <PinOff size={12} /> : <Pin size={12} />}
              </button>
            )}
          </div>
        )}

        {/* Emoji picker */}
        {pickerOpen && (
          <div
            onClick={(e) => e.stopPropagation()}
            className={`absolute z-10 top-6 ${mine ? "right-0" : "left-0"} bg-white border border-gray-200 rounded-full shadow-lg flex items-center gap-0.5 px-1.5 py-1`}
            data-testid={`emoji-picker-${msg.id}`}
          >
            {EMOJI_REACTIONS.map((e) => (
              <button key={e} onClick={() => onReact(msg.id, e)}
                className="text-base hover:scale-125 transition-transform px-1" data-testid={`emoji-${msg.id}-${e}`}>
                {e}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Render body text with lightweight @mention highlight so staff spot them at a glance.
function renderBodyWithMentions(body, myKey, mine) {
  const parts = [];
  const re = /@([A-Za-z0-9_.@-]+)/g;
  let lastIdx = 0;
  let m;
  let i = 0;
  while ((m = re.exec(body))) {
    if (m.index > lastIdx) parts.push(body.slice(lastIdx, m.index));
    const key = m[1];
    const isMe = myKey && key === myKey;
    parts.push(
      <span key={`m-${i++}`}
        className={`font-semibold ${isMe ? "bg-yellow-200 text-black px-1 rounded" : mine ? "text-white underline" : "text-indigo-600"}`}>
        @{key}
      </span>,
    );
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < body.length) parts.push(body.slice(lastIdx));
  return parts.length ? parts : body;
}

// ─────────────────────────────────────────────────────────────
// Attachment Bubble — image inline, audio inline player, docs as download
// ─────────────────────────────────────────────────────────────
const AttachmentBubble = ({ attachment, authAxios, mine }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState(false);
  const isImage = attachment.kind === "image" || (attachment.content_type || "").startsWith("image/");
  const isAudio = attachment.kind === "audio" || (attachment.content_type || "").startsWith("audio/");

  useEffect(() => {
    if (!isImage && !isAudio) return;
    let cancelled = false;
    let url;
    (async () => {
      try {
        const res = await authAxios.get(`${API_URL}${attachment.url}`, { responseType: "blob" });
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setBlobUrl(url);
      } catch { if (!cancelled) setErr(true); }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [attachment, authAxios, isImage, isAudio]);

  const download = async () => {
    try {
      const res = await authAxios.get(`${API_URL}${attachment.url}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = attachment.filename || "download";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("Could not download file"); }
  };

  if (isImage) {
    if (err) return <p className={`text-[10px] italic ${mine ? "text-white/70" : "text-gray-400"}`}>Image failed to load</p>;
    if (!blobUrl) return <div className={`rounded-lg h-24 w-32 flex items-center justify-center ${mine ? "bg-white/10" : "bg-gray-100"}`}><Loader2 size={14} className="animate-spin opacity-60" /></div>;
    return (
      <button onClick={download} className="block" data-testid={`attachment-img-${attachment.id}`}>
        <img src={blobUrl} alt={attachment.filename} className="rounded-lg max-h-48 max-w-[220px] object-cover border border-black/5" />
      </button>
    );
  }

  if (isAudio) {
    if (err) return <p className={`text-[10px] italic ${mine ? "text-white/70" : "text-gray-400"}`}>Voice note failed to load</p>;
    if (!blobUrl) return <div className={`rounded-lg h-9 w-40 flex items-center justify-center ${mine ? "bg-white/10" : "bg-gray-100"}`}><Loader2 size={14} className="animate-spin opacity-60" /></div>;
    return (
      <div className={`rounded-lg px-2 py-1 ${mine ? "bg-white/10" : "bg-gray-100"}`} data-testid={`attachment-audio-${attachment.id}`}>
        <audio controls src={blobUrl} className="h-8 max-w-[220px]" />
      </div>
    );
  }

  return (
    <button onClick={download}
      className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] ${mine ? "bg-white/15 hover:bg-white/25" : "bg-gray-100 hover:bg-gray-200"}`}
      data-testid={`attachment-file-${attachment.id}`}>
      <FileText size={11} />
      <span className="truncate max-w-[160px]">{attachment.filename}</span>
      <Download size={11} className="flex-shrink-0" />
    </button>
  );
};

const TabBtn = ({ active, onClick, icon, label, badge, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`flex-1 py-2 text-xs font-semibold flex items-center justify-center gap-1 relative ${active ? "text-indigo-600 border-b-2 border-indigo-500 bg-white" : "text-gray-500"}`}>
    {icon} {label}
    {badge ? <span className="absolute top-1 right-2 bg-[#E83E8C] text-white text-[9px] rounded-full px-1 min-w-[14px] h-[14px] flex items-center justify-center">{badge > 99 ? "99+" : badge}</span> : null}
  </button>
);

export default InternalChatWidget;
